import React, { FC, PropsWithChildren } from "react";

import { t } from "i18next";
import styled from "styled-components";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import { IconButton } from "@mui/material";

import { breakpoints } from "@/styles/breakpoints";

export type BannerType = "info" | "warning";

type BannerProps = {
    type: BannerType;
    fullWidth: boolean;
    onClose?: () => void;
};

const getColor = (type: BannerType) => {
    return type === "warning" ? "var(--color-warning)" : "var(--color-info)";
};

const getBackgroundColor = (type: BannerType) => {
    return type === "warning" ? "var(--color-warning-light)" : "var(--color-info-light)";
};

const StyledBanner = styled.div<{ $bannerType: BannerType; $fullWidth: boolean }>`
    display: flex;
    align-items: center;
    justify-content: ${(props) => (props.$fullWidth ? "center" : "space-between")};
    padding: 10px 16px;
    font-size: var(--size-16);
    @media ${breakpoints.xs} {
        font-size: var(--size-14);
    }
    line-height: 24px;
    text-align: center;
    border-radius: ${(props) => (props.$fullWidth ? 0 : "var(--radius-10)")};
    color: ${(props) => getColor(props.$bannerType)};
    background: ${(props) => getBackgroundColor(props.$bannerType)};

    svg {
        path {
            color: ${(props) => getColor(props.$bannerType)};
        }
    }
`;

const Banner: FC<PropsWithChildren<BannerProps>> = ({ type, onClose, children, fullWidth }) => {
    const getIcon = () => {
        return type === "warning" ? (
            <ReportProblemOutlinedIcon fontSize="large" />
        ) : (
            <InfoOutlinedIcon fontSize="large" />
        );
    };

    return (
        <StyledBanner $bannerType={type} $fullWidth={fullWidth} role="alert">
            {getIcon()}
            <div className="message">{children}</div>
            {onClose && (
                <IconButton onClick={onClose} aria-label={t("Common.Close")}>
                    <CloseIcon fontSize="large" />
                </IconButton>
            )}
        </StyledBanner>
    );
};

export default Banner;
